import { TFunction } from 'i18next'
import * as yup from 'yup'
import {
  PreorderDiskType,
  PreorderHardware,
  PreorderHardwarePayloads,
  PreorderHardwareType,
} from '../../../api/preorderHardwareService'

export const hardwarePath = '/dedicated-servers/preorder/hardware'
export const hardwareTypes: PreorderHardwareType[] = ['cpus', 'disks', 'rams']
export const diskTypes: PreorderDiskType[] = ['HDD', 'SSD', 'NVME']

export const isHardwareType = (value?: string | null): value is PreorderHardwareType => (
  hardwareTypes.includes(value as PreorderHardwareType)
)

export type HardwareFormValues = {
  name: string;
  model: string;
  description: string;
  is_active: boolean;
  cores: string;
  threads: string;
  speed_mhz: string;
  turbo_speed_mhz: string;
  disk_type: PreorderDiskType;
  size_gb: string;
  size_mb: string;
}

type NumericField = {
  key: 'cores' | 'threads' | 'speed_mhz' | 'turbo_speed_mhz' | 'size_gb' | 'size_mb';
  label: string;
  max: number;
}

export const hardwareConfig: Record<PreorderHardwareType, {
  label: string;
  create: string;
  edit: string;
  fields: NumericField[];
}> = {
  cpus: {
    label: 'PREORDER_CPUS', create: 'PREORDER_ADD_CPU', edit: 'PREORDER_EDIT_CPU',
    fields: [
      { key: 'cores', label: 'DEDICATED_CPU_CORES', max: 65535 },
      { key: 'threads', label: 'DEDICATED_CPU_THREADS', max: 65535 },
      { key: 'speed_mhz', label: 'PREORDER_SPEED_MHZ', max: 4294967295 },
      { key: 'turbo_speed_mhz', label: 'PREORDER_TURBO_SPEED_MHZ', max: 4294967295 },
    ],
  },
  disks: {
    label: 'PREORDER_DISKS', create: 'PREORDER_ADD_DISK', edit: 'PREORDER_EDIT_DISK',
    fields: [{ key: 'size_gb', label: 'PREORDER_SIZE_GB', max: 4294967295 }],
  },
  rams: {
    label: 'RAM', create: 'PREORDER_ADD_RAM', edit: 'PREORDER_EDIT_RAM',
    fields: [{ key: 'size_mb', label: 'PREORDER_SIZE_MB', max: 4294967295 }],
  },
}

export const initialHardwareValues = (item?: PreorderHardware): HardwareFormValues => ({
  name: item?.name ?? '',
  model: item?.model ?? '',
  description: item?.description ?? '',
  is_active: item?.is_active ?? true,
  cores: item && 'cores' in item ? String(item.cores) : '',
  threads: item && 'threads' in item ? String(item.threads) : '',
  speed_mhz: item && 'speed_mhz' in item ? String(item.speed_mhz) : '',
  turbo_speed_mhz: item && 'turbo_speed_mhz' in item ? String(item.turbo_speed_mhz) : '',
  disk_type: item && 'disk_type' in item ? item.disk_type : 'SSD',
  size_gb: item && 'size_gb' in item ? String(item.size_gb) : '',
  size_mb: item && 'size_mb' in item ? String(item.size_mb) : '',
})

export const hardwareSchema = (type: PreorderHardwareType, t: TFunction) => {
  const required = String(t('PREORDER_REQUIRED'))
  const text = yup.string().trim().required(required)
    .max(255, `${t('STRING_VALIDATE_MAX')} 255 ${t('CHARACTERS')}`)
  const numericFields = Object.fromEntries(hardwareConfig[type].fields.map((field) => [
    field.key,
    yup.number()
      .transform((value, original) => original === '' ? undefined : value)
      .typeError(String(t('PREORDER_INTEGER_RANGE', { max: field.max })))
      .integer(String(t('PREORDER_INTEGER_RANGE', { max: field.max })))
      .min(0, String(t('PREORDER_INTEGER_RANGE', { max: field.max })))
      .max(field.max, String(t('PREORDER_INTEGER_RANGE', { max: field.max })))
      .required(required),
  ]))

  return yup.object({
    name: text,
    model: text,
    description: yup.string(),
    is_active: yup.boolean().required(),
    ...numericFields,
    ...(type === 'disks' ? { disk_type: yup.string().oneOf(diskTypes).required(required) } : {}),
  })
}

export const hardwarePayload = (
  type: PreorderHardwareType,
  values: HardwareFormValues,
): PreorderHardwarePayloads[PreorderHardwareType] => {
  const common = {
    name: values.name.trim(),
    model: values.model.trim(),
    description: values.description,
    is_active: values.is_active,
  }
  switch (type) {
    case 'cpus':
      return {
        ...common,
        cores: Number(values.cores),
        threads: Number(values.threads),
        speed_mhz: Number(values.speed_mhz),
        turbo_speed_mhz: Number(values.turbo_speed_mhz),
      }
    case 'disks':
      return { ...common, disk_type: values.disk_type, size_gb: Number(values.size_gb) }
    case 'rams':
      return { ...common, size_mb: Number(values.size_mb) }
  }
}
